/**
 * What the header's Notifications panel shows, built only from real sources:
 *
 *   approvals        GET /security/api/v1/incidents/approvals?status=pending&mine=true
 *                    (approval-gated response steps routed to this person)
 *   recommendations  GET /recommendations/api/v1/recommendations (open statuses),
 *                    narrowed here to the ones whose `assignee` is this person
 *   alerts           GET /alerts/api/v1/alerts/history?unread_only=true
 *
 * Every item is normalised to one shape so the panel can group, filter and
 * count without knowing where it came from. Nothing is fabricated: a source
 * that failed is reported by the panel as "Couldn't load …", never as empty.
 *
 * Module scope — "only modules you can open" — reuses the sidebar's own
 * entitlement rules (SECTION_MODULE_SLUGS via isSectionEntitled), so the panel
 * can never offer a module the navigation would hide.
 */
import {
  isSectionEntitled,
  shouldApplyModuleEntitlements,
} from '../Sidebar/productConfig';
import STATIC_MODULE_CATALOG from '../../../config/modules.generated';
import { indexCatalog, licensedModuleSet } from '../../../licensing/moduleLicense';

const MODULE_INDEX = indexCatalog(STATIC_MODULE_CATALOG);

/**
 * Display modules. `sections` are sidebar section keys: a module is openable
 * when ANY of them is entitled for this org, exactly as the sidebar decides.
 * Colours are the module accents from the approved mockup.
 */
export const NOTIF_MODULES = [
  {
    id: 'security', labelKey: 'moduleSecurity', label: 'Security',
    color: '#FF8FA8', tint: 'rgba(255, 107, 142, 0.14)', icon: 'shield',
    sections: ['security'],
  },
  {
    id: 'atlas', labelKey: 'moduleAtlas', label: 'Atlas',
    color: '#C9C0FF', tint: 'rgba(123, 104, 238, 0.16)', icon: 'globe',
    sections: [
      'agent-atlas', 'custom-agents', 'monitoring', 'enterprise-ai', 'copilot-studio',
      'gemini-workspace', 'gemini-enterprise-agents', 'saas-ai-platform',
    ],
  },
  {
    id: 'code', labelKey: 'moduleCode', label: 'Code Security',
    color: '#7FE3D3', tint: 'rgba(70, 217, 197, 0.14)', icon: 'code',
    sections: ['sast'],
  },
  {
    id: 'finops', labelKey: 'moduleFinops', label: 'FinOps',
    color: '#FCD34D', tint: 'rgba(251, 191, 36, 0.14)', icon: 'dollar-sign',
    sections: ['finops'],
  },
  {
    id: 'cloud', labelKey: 'moduleCloud', label: 'Cloud',
    color: '#93B4FF', tint: 'rgba(88, 155, 255, 0.14)', icon: 'cloud',
    sections: ['cloud-ai'],
  },
  {
    id: 'shadow', labelKey: 'moduleShadow', label: 'Shadow AI',
    color: '#FDBA74', tint: 'rgba(249, 115, 22, 0.14)', icon: 'eye',
    sections: ['shadow-ai', 'endpoint-management', 'mdm', 'edr', 'ai-gateway'],
  },
];

/** An alert whose producer nobody has mapped yet — shown, neutrally, never hidden. */
export const OTHER_MODULE = {
  id: 'other', labelKey: 'moduleOther', label: 'Other',
  color: '#AAB3BB', tint: 'rgba(170, 179, 187, 0.14)', icon: 'bell',
  sections: [],
};

const MODULE_BY_ID = Object.fromEntries(NOTIF_MODULES.map((m) => [m.id, m]));

/**
 * Raw alert `module` values (the keyword the producers write) → display module,
 * the sidebar section that has to be entitled to open it, and where its list
 * lives. `alertPage` pages that render ModuleAlerts accept `?alert_id=` and
 * open that alert's details; the rest land on the list.
 */
const ALERT_MODULES = {
  security: { module: 'security', section: 'security', alertPage: '/agent-atlas/alerts?surface=security', deepLink: true },
  finops: { module: 'finops', section: 'finops', alertPage: '/agent-atlas/alerts?surface=finops', deepLink: true },
  monitoring: { module: 'atlas', section: 'monitoring', alertPage: '/observability/alerts?category=monitoring', deepLink: true },
  agent: { module: 'atlas', section: 'monitoring', alertPage: '/observability/alerts?category=monitoring', deepLink: true },
  copilot: { module: 'atlas', section: 'enterprise-ai', alertPage: '/copilot/alerts', deepLink: true },
  microsoft_copilot: { module: 'atlas', section: 'enterprise-ai', alertPage: '/copilot/alerts', deepLink: true },
  copilot_studio: { module: 'atlas', section: 'copilot-studio', alertPage: '/copilot/studio/alerts', deepLink: true },
  copilotstudio: { module: 'atlas', section: 'copilot-studio', alertPage: '/copilot/studio/alerts', deepLink: true },
  gemini: { module: 'atlas', section: 'gemini-workspace', alertPage: '/gemini/alerts', deepLink: true },
  gemini_enterprise: { module: 'atlas', section: 'gemini-enterprise-agents', alertPage: '/gemini/agents/alerts', deepLink: true },
  // AI Providers: the Atlas Alerts page pinned to the provider opens ?alert_id= deep links.
  openai: { module: 'atlas', section: 'saas-ai-platform', alertPage: '/saas-ai/alerts?vendor=openai', deepLink: true },
  anthropic: { module: 'atlas', section: 'saas-ai-platform', alertPage: '/saas-ai/alerts?vendor=anthropic', deepLink: true },
  cursor: { module: 'atlas', section: 'saas-ai-platform', alertPage: '/saas-ai/alerts?vendor=cursor', deepLink: true },
  google_antigravity: { module: 'atlas', section: 'saas-ai-platform', alertPage: '/saas-ai/alerts?vendor=google_antigravity', deepLink: true },
  cloud_discovery: { module: 'cloud', section: 'cloud-ai', alertPage: '/cloud/notifications' },
  cloud: { module: 'cloud', section: 'cloud-ai', alertPage: '/cloud/notifications' },
  sast: { module: 'code', section: 'sast', alertPage: '/sast/overview' },
  code_security: { module: 'code', section: 'sast', alertPage: '/sast/overview' },
  shadow_ai: { module: 'shadow', section: 'shadow-ai', alertPage: '/alerts' },
  browser_extension: { module: 'shadow', section: 'endpoint-management', alertPage: '/alerts' },
  extension: { module: 'shadow', section: 'endpoint-management', alertPage: '/alerts' },
  endpoint: { module: 'shadow', section: 'shadow-ai', alertPage: '/alerts' },
  mdm: { module: 'shadow', section: 'mdm', alertPage: '/alerts' },
  edr: { module: 'shadow', section: 'edr', alertPage: '/alerts' },
  gateway: { module: 'shadow', section: 'ai-gateway', alertPage: '/alerts' },
  ai_gateway: { module: 'shadow', section: 'ai-gateway', alertPage: '/alerts' },
};

/** Recommendation engine `module` → display module. Every one opens in the Inbox. */
const REC_MODULES = {
  sast: 'code',
  cloud: 'cloud',
  security: 'security',
};

const norm = (v) => String(v ?? '').trim().toLowerCase().replace(/-/g, '_');

/**
 * The entitlement check, built once per render from the same inputs the
 * sidebar uses. A super admin, or an org whose module list is empty, sees every
 * module — again matching the sidebar (`applyModuleFilter`).
 */
export const makeCanOpen = ({ moduleSlugs = [], roleName = '' } = {}) => {
  // Canonical slugs (aliases resolved) plus the core modules every tenant holds.
  const set = licensedModuleSet(moduleSlugs, MODULE_INDEX);
  const isSuperAdmin = String(roleName || '').toLowerCase() === 'super admin';
  const apply = shouldApplyModuleEntitlements(moduleSlugs) && !isSuperAdmin;
  const canOpenSection = (section) => !apply || !section || isSectionEntitled(section, set);
  const canOpenModule = (mod) => !apply || mod.id === 'other'
    || mod.sections.some((s) => isSectionEntitled(s, set));
  return { canOpenSection, canOpenModule };
};

/** Epoch ms from an ISO string, epoch seconds or epoch ms. NaN when absent. */
export const toMs = (ts) => {
  if (ts == null || ts === '') return NaN;
  if (typeof ts === 'number') return ts < 1e12 ? ts * 1000 : ts;
  if (/^\d+(\.\d+)?$/.test(String(ts))) return toMs(Number(ts));
  return new Date(ts).getTime();
};

export const classifyAlert = (alert) => {
  const raw = norm(alert?.module || alert?.metadata?.module);
  const spec = ALERT_MODULES[raw];
  const module = spec ? MODULE_BY_ID[spec.module] : OTHER_MODULE;
  const id = alert?.alert_id ?? alert?.id ?? alert?._id;
  let href = spec?.alertPage || '/alerts';
  if (spec?.deepLink && id != null) {
    href += `${href.includes('?') ? '&' : '?'}alert_id=${encodeURIComponent(id)}`;
  }
  return { module, section: spec?.section || null, href, id };
};

export const classifyRec = (rec) => MODULE_BY_ID[REC_MODULES[norm(rec?.module)] || 'atlas'];

/** Internal app path, or null — an absolute URL must not ride a router <Link>. */
const internalPath = (href) => (typeof href === 'string' && /^\/(?!\/)/.test(href) ? href : null);

/**
 * Normalise all three sources into one list.
 *
 * @returns {Array<{ key, kind, module, title, sub, ts, unread, needsYou,
 *   primary: {label, to}|null, open: {label, to}, raw }>}
 */
export const buildNotifications = ({
  approvals = [], recs = [], alerts = [], canOpenSection = () => true, t = (k, o) => o?.defaultValue || k,
}) => {
  const out = [];

  approvals.forEach((a) => {
    if (!a || !a.incident_id) return;
    const st = a.step || {};
    const step = st.title || st.action || t('responseStep', { defaultValue: 'Response step' });
    const incidentHref = `/security-atlas/incidents/${encodeURIComponent(a.incident_id)}`;
    out.push({
      key: `approval:${a.id ?? a.incident_id}`,
      kind: 'approval',
      module: MODULE_BY_ID.security,
      title: t('approvalTitle', { step, defaultValue: `Approve: ${step}` }),
      sub: t('approvalSub', {
        incident: a.incident_title || a.incident_id,
        defaultValue: `${a.incident_title || a.incident_id} · sign off to run it`,
      }),
      due: a.overdue ? (a.due_label || null) : null,
      ts: toMs(a.ts),
      unread: true,
      needsYou: true,
      primary: { label: t('reviewAndApprove', { defaultValue: 'Review and approve' }), to: incidentHref },
      open: { label: t('open', { defaultValue: 'Open' }), to: '/security-atlas/response' },
      raw: a,
    });
  });

  recs.forEach((r) => {
    if (!r || !r.id) return;
    const inbox = `/agent-atlas/inbox?rec=${encodeURIComponent(r.id)}`;
    const actionTo = r.action?.type === 'navigate' ? internalPath(r.action.href) : null;
    out.push({
      key: `rec:${r.id}`,
      kind: 'rec',
      module: classifyRec(r),
      title: r.title || t('assignedFinding', { defaultValue: 'Finding assigned to you' }),
      sub: r.assigned_by
        ? t('assignedBy', { who: r.assigned_by, defaultValue: `Assigned to you by ${r.assigned_by}` })
        : t('assignedToYou', { defaultValue: 'Assigned to you' }),
      ts: toMs(r.assigned_at || r.last_material_change_at || r.first_seen),
      unread: r.read !== true,
      needsYou: true,
      primary: actionTo && r.action?.label
        ? { label: r.action.label, to: actionTo }
        : { label: t('openInInbox', { defaultValue: 'Open in Inbox' }), to: inbox },
      open: actionTo && r.action?.label ? { label: t('open', { defaultValue: 'Open' }), to: inbox } : null,
      raw: r,
    });
  });

  alerts.forEach((a) => {
    if (!a) return;
    const c = classifyAlert(a);
    // A mapped module the user cannot open is not theirs to see here.
    if (c.section && !canOpenSection(c.section)) return;
    const title = a.title || a.message || a.description
      || t('alertRaised', { defaultValue: 'Alert raised' });
    const sub = a.message && a.title && a.message !== a.title
      ? a.message
      : (a.metadata?.threat_summary || a.metadata?.detail || '');
    out.push({
      key: `alert:${c.id ?? title}`,
      kind: 'alert',
      module: c.module,
      title,
      sub,
      severity: a.severity ? String(a.severity).toLowerCase() : null,
      ts: toMs(a.created_at || a.timestamp || a['@timestamp']),
      unread: !a.is_read,
      needsYou: false,
      primary: null,
      open: { label: t('open', { defaultValue: 'Open' }), to: c.href },
      raw: a,
    });
  });

  return out;
};

const startOfToday = (now) => {
  const d = new Date(now);
  d.setHours(0, 0, 0, 0);
  return d.getTime();
};

/** Needs you first (oldest ask first is wrong for a queue — newest first like everything else), then Today, then Earlier. */
export const groupNotifications = (items, now = Date.now()) => {
  const byNewest = (a, b) => (Number.isFinite(b.ts) ? b.ts : 0) - (Number.isFinite(a.ts) ? a.ts : 0);
  const today = startOfToday(now);
  const needs = items.filter((n) => n.needsYou).sort(byNewest);
  const rest = items.filter((n) => !n.needsYou).sort(byNewest);
  return [
    { id: 'needs', items: needs },
    // An alert with no usable timestamp is not "today" — we cannot say when it was.
    { id: 'today', items: rest.filter((n) => Number.isFinite(n.ts) && n.ts >= today) },
    { id: 'earlier', items: rest.filter((n) => !(Number.isFinite(n.ts) && n.ts >= today)) },
  ].filter((g) => g.items.length > 0);
};

/** "just now" / "5m" / "3h" / "Yesterday" / "4d" / a date — compact, for a row's meta line. */
export const relTime = (ms, t = (k, o) => o?.defaultValue || k, now = Date.now()) => {
  if (!Number.isFinite(ms)) return '';
  const s = Math.max(0, (now - ms) / 1000);
  if (s < 60) return t('justNow', { defaultValue: 'just now' });
  if (s < 3600) return t('minutesAgo', { n: Math.floor(s / 60), defaultValue: `${Math.floor(s / 60)}m` });
  if (ms >= startOfToday(now)) return t('hoursAgo', { n: Math.floor(s / 3600), defaultValue: `${Math.floor(s / 3600)}h` });
  if (ms >= startOfToday(now) - 86400000) return t('yesterday', { defaultValue: 'Yesterday' });
  const days = Math.max(2, Math.floor(s / 86400));
  return t('daysAgo', { n: days, defaultValue: `${days}d` });
};

/** Badge text: exact up to 99, and a trailing + when the server holds more than were loaded. */
export const badgeText = (n, more = false) => {
  if (!n) return '';
  if (n > 99) return '99+';
  return `${n}${more ? '+' : ''}`;
};
